import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, CreditCard, Eye } from 'lucide-react';
import { ProcessedInvoice, PaymentData } from '../../types';
import FormField from '../common/FormField';
import { useQRCode } from '../../hooks/useQRCode';
import PaymentPreviewModal from './PaymentPreviewModal';
import { validatePaymentData } from '../../utils/validation/paymentValidation';
import PaymentStatusBadge from './PaymentStatusBadge';

interface InvoiceCardProps {
  invoice: ProcessedInvoice;
  onDataChange: (id: string, data: PaymentData) => void;
}

export default function InvoiceCard({
  invoice,
  onDataChange
}: InvoiceCardProps) {
  const [showPayment, setShowPayment] = useState(false);
  const { qrCodeData } = useQRCode(invoice.data);
  const validation = validatePaymentData(invoice.data);

  const handleChange = (field: keyof PaymentData) => (value: string) => {
    onDataChange(invoice.id, {
      ...invoice.data,
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    });
  };

  const handleMarkAsPaid = () => {
    onDataChange(invoice.id, {
      ...invoice.data,
      status: 'paid'
    });
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-lg overflow-hidden h-full flex flex-col">
      <div className="p-6 flex-1 flex flex-col">
        {/* Status and Filename */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {validation.isValid ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            <span className="text-sm text-[#c8c2bd] truncate">
              {invoice.file.name}
            </span>
          </div>
          <PaymentStatusBadge status={invoice.data.status || 'pending'} />
        </div>

        {/* Form Fields */}
        <div className="space-y-4 flex-1">
          <FormField
            label="Recipient"
            value={invoice.data.name}
            onChange={handleChange('name')}
            placeholder="Company or person name"
            error={validation.errors.name}
            required
          />

          <FormField
            label="IBAN"
            value={invoice.data.iban}
            onChange={handleChange('iban')}
            placeholder="e.g., BE68539007547034"
            error={validation.errors.iban}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Amount"
              value={invoice.data.amount.toString()}
              onChange={handleChange('amount')}
              type="number"
              placeholder="0.00"
              error={validation.errors.amount}
              required
            />
            <FormField
              label="Due Date"
              value={invoice.data.dueDate || ''}
              onChange={handleChange('dueDate')}
              type="date"
            />
          </div>

          <FormField
            label="Reference"
            value={invoice.data.reference || ''}
            onChange={handleChange('reference')}
            error={validation.errors.reference}
          />
        </div>

        {/* QR Code and Action Buttons */}
        <div className="mt-6 space-y-4">
          {validation.isValid && qrCodeData && (
            <div className="bg-white rounded-lg shadow-lg p-3 flex flex-col items-center">
              <div className="text-xs font-medium text-gray-600 mb-1">
                Scan me in your bank app
              </div>
              <div className="flex items-center justify-center">
                <img 
                  src={qrCodeData} 
                  alt="Payment QR Code"
                  className="w-32 h-32 rounded"
                  draggable={false}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => setShowPayment(true)}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                validation.isValid
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {validation.isValid ? (
                <>
                  <CreditCard className="w-4 h-4" />
                  Check & Pay
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  View Errors
                </>
              )}
            </button>

            {invoice.data.status !== 'paid' && validation.isValid && (
              <button
                onClick={handleMarkAsPaid}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Mark as Paid
              </button>
            )}
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentPreviewModal
          invoice={invoice}
          onClose={() => setShowPayment(false)}
          onDataChange={onDataChange}
          isValid={validation.isValid}
        />
      )}
    </div>
  );
}